import { useTranslation } from 'react-i18next';
import { Compass } from 'lucide-react';
import { PipelineTemperatureWidget } from '@/components/dashboard/PipelineTemperatureWidget';
import { ForecastSummaryWidget } from '@/components/dashboard/ForecastSummaryWidget';
import { FunnelConversionWidget } from '@/components/dashboard/FunnelConversionWidget';
import { PipelineVelocityWidget } from '@/components/dashboard/PipelineVelocityWidget';
import { AlertsFeedWidget } from '@/components/dashboard/AlertsFeedWidget';
import { Card } from '@/components/ui/Card';
import { Link } from 'react-router-dom';

/**
 * Story 58.3 — Manager Command Center ("The Coach").
 *
 * Composed view: pipeline temperature + forecast (the two numbers a
 * manager opens with) on top, then funnel conversion + velocity side
 * by side, then the alerts feed. Drill-down lives via the existing
 * widget links + the Funnel Explorer for time-travel.
 *
 * Reuses widgets from epics 56 / 85 — this page is composition, not
 * new visual surface. Future iterations can layer per-rep heatmaps
 * and a deal inspection queue (A12-flagged opps).
 */
export function CockpitManagerPage() {
  const { t } = useTranslation();
  return (
    <div className="space-y-6 p-6">
      <header className="flex items-center gap-3">
        <Compass className="h-5 w-5 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-semibold">{t('cockpitManager.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('cockpitManager.subtitle')}</p>
        </div>
      </header>

      <PipelineTemperatureWidget />
      <ForecastSummaryWidget />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <FunnelConversionWidget />
        <PipelineVelocityWidget />
      </div>

      <AlertsFeedWidget />

      <Card>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('cockpitManager.deep_dive')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('cockpitManager.deep_dive_hint')}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Link
            to="/analytics/funnel"
            className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
          >
            {t('cockpitManager.open_funnel')}
          </Link>
          <Link
            to="/portfolio"
            className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
          >
            {t('cockpitManager.open_portfolio')}
          </Link>
          <Link
            to="/opportunities?stale=true"
            className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
          >
            {t('cockpitManager.open_stale')}
          </Link>
        </div>
      </Card>
    </div>
  );
}
